import Swiper from '../../../../../../ShopUi/Theme/default/scripts/utils/swiperMethods';
import { BREAKPOINTS } from '../../../../../../ShopUi/Theme/default/scripts/config';
import { EAudioEvents } from '../../../../../../ShopUi/Theme/default/scripts/modules/events/audioEvents';
import { EDialogEvents } from '../../../../../../ShopUi/Theme/default/scripts/modules/events/dialogEvent';
import { EMediaCenterEvents } from '../../../../../../ShopUi/Theme/default/scripts/modules/events/mediaCenterEvents';
import { EVideoEvents } from '../../../../../../ShopUi/Theme/default/scripts/modules/events/videoEvents';
import Component from 'ShopUi/models/component';
import EventBus from 'PyzShopUi/scripts/utils/eventBus';
import('./article-mediacenter.scss');

class MediaCenter extends Component {
    private static readonly VIDEO_DIALOG = {
        OPEN_TIMEOUT: 1500,
    };

    private static readonly AUDIO_DIALOG = {
        OPEN_TIMEOUT: 1500,
    };

    private readonly mediaCenterWrapper: HTMLElement;
    private eventBus: EventBus;
    private swiperMain: Swiper;
    private swiperThumbs?: Swiper;
    private swiperMainElement: HTMLElement;
    private swiperMainSlideElements: NodeListOf<HTMLElement>;
    private swiperMainScrollbarElement: HTMLElement;
    private swiperThumbsElement: HTMLElement;
    private swiperThumbsSlideElements: NodeListOf<HTMLElement>;
    private swiperThumbsScrollbarElement: HTMLElement;
    private videoDialogTrigger: HTMLElement;
    private videoDialogId: string;
    private videoDialogContainer: HTMLElement;
    private videoDialogOpenTimeoutId: number;
    private audioDialogTrigger: HTMLElement;
    private audioDialogId: string;
    private audioDialogContainer: HTMLElement;
    private audioDialogOpenTimeoutId: number;
    private scaleTrigger: HTMLElement;

    addEventListeners = (): void => {
        this.eventBus.subscribe('window:resize:debounced', this.setSwiperThumbHeight);

        if (this.videoDialogTrigger) {
            this.videoDialogTrigger.addEventListener('click', this.openVideoDialog);
            this.videoDialogTrigger.addEventListener('keydown', e => {
                if (e.key === 'Enter') {
                    this.openVideoDialog();
                }
            });
            this.eventBus.subscribe(EVideoEvents.META_LOADED, this.checkAndPublishVideoDialogOpen);
        }

        if (this.audioDialogTrigger) {
            this.audioDialogTrigger.addEventListener('click', this.openAudioDialog);
            this.audioDialogTrigger.addEventListener('keydown', e => {
                if (e.key === 'Enter') {
                    this.openAudioDialog();
                }
            });
            this.eventBus.subscribe(EAudioEvents.META_LOADED, this.checkAndPublishAudioDialogOpen);
        }

        if (this.scaleTrigger) {
            this.scaleTrigger.addEventListener('click', this.toggleMediaCenterScale);
            this.scaleTrigger.addEventListener('keydown', e => {
                if (e.key === 'Enter') {
                    this.toggleMediaCenterScale();
                }
            });
        }
    }

    protected readyCallback(): void {
    }

    init() {
        this.eventBus = EventBus.getInstance();

        this.swiperMainElement = this.querySelector('.swiper-main .swiper-container');
        this.swiperMainScrollbarElement = this.querySelector('.swiper-main .swiper-scrollbar');
        this.swiperMainSlideElements = this.swiperMainElement.querySelectorAll('.swiper-slide');
        this.swiperThumbsElement = this.querySelector('.swiper-thumbs .swiper-container');
        this.swiperThumbsSlideElements = this.swiperThumbsElement.querySelectorAll('.swiper-slide');
        this.swiperThumbsScrollbarElement = this.querySelector('.swiper-thumbs .swiper-scrollbar');

        this.videoDialogTrigger = this.querySelector('[data-video-dialog-trigger]');
        if (this.videoDialogTrigger) {
            this.videoDialogId = this.videoDialogTrigger.getAttribute('data-video-dialog-trigger');
            this.videoDialogContainer = this.querySelector(`#${this.videoDialogId}`);
        }

        this.audioDialogTrigger = this.querySelector('[data-audio-dialog-trigger]');
        if (this.audioDialogTrigger) {
            this.audioDialogId = this.audioDialogTrigger.getAttribute('data-audio-dialog-trigger');
            this.audioDialogContainer = this.querySelector(`#${this.audioDialogId}`);
        }

        this.scaleTrigger = this.querySelector('.article-mediacenter-scale');

        this.addEventListeners();

        this.initThumbsSwiper();
        this.initMainSwiper();
        this.setSwiperThumbHeight();

        this.swiperMain.on('slideChange', this.resetScale);
        this.swiperMain.on('doubleTap', this.toggleMediaCenterScaleIcon);
    }

    private readonly initMainSwiper = (): void => {
        const options = {
            zoom: true,
            threshold: 10,
            scrollbar: {
                el: (this.swiperMainScrollbarElement),
            },
            preloadImages: false,
            lazy: true,
            watchSlidesVisibility: true,
            grabCursor: true,
            navigation: {
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
            },
            thumbs: {
                swiper: this.swiperThumbs
            }
        };

        this.swiperMain = new Swiper((this.swiperMainElement), options);
    }

    private readonly initThumbsSwiper = (): void => {
        this.swiperThumbs = new Swiper((this.swiperThumbsElement), {
            direction: 'vertical',
            slidesPerView: 'auto',
            spaceBetween: 10,
            threshold: 10,
            freeMode: true,
            watchSlidesProgress: true,
            grabCursor: true,
            scrollbar: {
                el: (this.swiperThumbsScrollbarElement),
                hide: true,
            },
            mousewheel: true,
        });
    }

    private readonly setSwiperThumbHeight = (): void => {
        if (BREAKPOINTS.minMedium()) {
            this.swiperThumbsElement.style.maxHeight = `${this.swiperMainElement.scrollHeight}px`;

            if (this.swiperThumbs) {
                this.swiperThumbs.update();
            } else {
                this.initThumbsSwiper();
            }
        }
    }

    private readonly toggleMediaCenterScale = (): void => {
        this.swiperMain.zoom.toggle();
        this.toggleMediaCenterScaleIcon();
    }

    private readonly toggleMediaCenterScaleIcon = (): void => {
        if (this.swiperMain.zoom.scale > 1) {
            this.scaleTrigger.querySelector('.article-mediacenter-scale-down').classList.remove('hide');
            this.scaleTrigger.querySelector('.article-mediacenter-scale-up').classList.add('hide');
        } else {
            this.scaleTrigger.querySelector('.article-mediacenter-scale-up').classList.remove('hide');
            this.scaleTrigger.querySelector('.article-mediacenter-scale-down').classList.add('hide');
        }
    }

    private readonly resetScale = (): void => {
        this.scaleTrigger.querySelector('.article-mediacenter-scale-up').classList.remove('hide');
        this.scaleTrigger.querySelector('.article-mediacenter-scale-down').classList.add('hide');
    }

    private readonly openVideoDialog = (): void => {
        this.eventBus.publish(EVideoEvents.PRELOAD, this.videoDialogContainer);

        this.videoDialogOpenTimeoutId = window.setTimeout((): void => {
            this.eventBus.publish(EDialogEvents.OPEN_DIALOG, { id: this.videoDialogId });
        }, MediaCenter.VIDEO_DIALOG.OPEN_TIMEOUT);

        this.eventBus.publish(EVideoEvents.META_LOADED, this.videoDialogContainer);
    }

    private readonly checkAndPublishVideoDialogOpen = (): void => {
        const videos = this.videoDialogContainer.querySelectorAll('video');
        let loadedVideos = 0;

        videos.forEach((video: HTMLVideoElement): void => {
            loadedVideos += (video.readyState > 0 ? 1 : 0);
        });

        if (videos.length === loadedVideos) {
            this.eventBus.publish(EDialogEvents.OPEN_DIALOG, { id: this.videoDialogId });
            window.clearTimeout(this.videoDialogOpenTimeoutId);
        }

        this.eventBus.subscribe(EDialogEvents.DIALOG_CLOSED, (target: HTMLElement) => {
            if (target.getAttribute('id') === this.videoDialogId) {
                videos.forEach((video: HTMLVideoElement): void => {
                    video.pause();
                    video.currentTime = 0;
                });
            }
        });
    }

    private readonly openAudioDialog = (): void => {
        this.eventBus.publish(EAudioEvents.PRELOAD, this.audioDialogContainer);

        this.audioDialogOpenTimeoutId = window.setTimeout((): void => {
            this.eventBus.publish(EDialogEvents.OPEN_DIALOG, { id: this.audioDialogId });
        }, MediaCenter.AUDIO_DIALOG.OPEN_TIMEOUT);

        this.eventBus.publish(EAudioEvents.META_LOADED, this.audioDialogContainer);
    }

    private readonly checkAndPublishAudioDialogOpen = (): void => {
        const audios = this.audioDialogContainer.querySelectorAll('audio');
        let loadedAudios = 0;

        audios.forEach((audio: HTMLAudioElement): void => {
            loadedAudios += (audio.readyState > 0 ? 1 : 0);
        });

        if (audios.length === loadedAudios) {
            this.eventBus.publish(EDialogEvents.OPEN_DIALOG, { id: this.audioDialogId });
            window.clearTimeout(this.audioDialogOpenTimeoutId);
        }

        this.eventBus.subscribe(EDialogEvents.DIALOG_CLOSED, (target: HTMLElement) => {
            if (target.getAttribute('id') === this.audioDialogId) {
                audios.forEach((audio: HTMLAudioElement): void => {
                    audio.pause();
                    audio.currentTime = 0;
                });
            }
        });
    }
}

/* tslint:disable:max-file-line-count */
export default MediaCenter;
